import { createApi } from '@reduxjs/toolkit/query/react'
import { ConsentType, ExternalUserConsentChange, ExternalUserCreateUpdatePayload, User } from '../../models/User'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { removeEmptyItems } from '../../common/util'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { mediaApi } from '../media/media'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface GetExternalUserByIdRequest extends BaseRequest {
  userId: string
}

export interface CreateExternalUserRequest extends BaseRequest {
  userData: ExternalUserCreateUpdatePayload
}

export interface SaveExternalUserByIdRequest extends BaseRequest {
  userId: string
  userData: ExternalUserCreateUpdatePayload
}

export interface UpdateExternalUserRolesRequest extends BaseRequest {
  userId: string
  roleIds: string[]
}

export interface DeleteExternalUserRequest extends BaseRequest {
  userId: string
}

export interface GetExternalUserRequest extends BasePageRequest {
  name?: string
  ownerId?: string
  emails?: string[]
  orderBy?: string
}

export interface GetAllExternalUsersRequest extends BaseRequest {
  name?: string
  ownerId?: string
  emails?: string[]
  externalId?: string
  externalAccountId?: string
  serviceId?: string
}

export interface GetExternalUserConsentHistoryRequest extends BasePageRequest {
  userId: string
  sessionId?: string
  roomId?: string
}

export interface UpdateExternalUserConsentRequest extends BaseRequest {
  userId: string
  roomId?: string
  sessionId?: string
  newValue?: string
  oldValue?: string
  changedByUserId?: string
  reason?: string
  attachmentFileId?: string
  type: ConsentType
}

export interface UpdateExternalUserConsentRequest extends BaseRequest {
  userId: string
  roomId?: string
  sessionId?: string
  newValue?: string
  oldValue?: string
  changedByUserId?: string
  reason?: string
  attachmentFileId?: string
  type: ConsentType
}

export interface SearchExternalUserRequest extends BasePageRequest {
  term?: string
  ownerId?: string
  orderBy?: string
}

export const externalUserApi = createApi({
  reducerPath: 'externalUserApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['ExternalUserConsentHistory'],
  endpoints: (builder) => ({
    getExternalUsers: builder.query<BasePageResponse<User[]>, GetExternalUserRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/external-users`,
          params: {
            name: request.name,
            ownerId: request.ownerId,
            ...(request.emails ? { emails: request.emails.join(',') } : {}),
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.orderBy ? { orderBy: request.orderBy } : {}),
          },
        }
      },
    }),
    getAllExternalUsers: builder.query<BaseResponse<User[]>, GetAllExternalUsersRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<User>(
          {
            url: `/accounts/${request.accountId}/external-users`,
            params: {
              name: request.name,
              ...(request.emails ? { emails: request.emails.join(',') } : {}),
              ...(request.serviceId ? { serviceId: request.serviceId } : {}),
              ...(request.externalAccountId ? { externalAccountId: request.externalAccountId } : {}),
              ...(request.externalId ? { externalId: request.externalId } : {}),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    getExternalUserById: builder.query<BaseResponse<User>, GetExternalUserByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/external-users/${request.userId}`,
    }),
    createExternalUser: builder.mutation<BaseResponse<User>, CreateExternalUserRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/external-users/`,
        method: 'POST',
        body: removeEmptyItems(request.userData),
      }),
    }),
    saveExternalUserById: builder.mutation<BaseResponse<User>, SaveExternalUserByIdRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/external-users/${request.userId}`,
        method: 'PATCH',
        body: { ...removeEmptyItems(request.userData), owner: request.userData.owner },
      }),
    }),
    deleteExternalUserById: builder.mutation<BaseResponse<User>, DeleteExternalUserRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/external-users/${request.userId}`,
        method: 'DELETE',
      }),
    }),
    updateExternalUserRoles: builder.mutation<BaseResponse<User>, UpdateExternalUserRolesRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/external-users/${request.userId}/roles`,
        method: 'PUT',
        body: { roleIds: request.roleIds },
      }),
    }),
    getExternalUserConsentHistory: builder.query<BasePageResponse<ExternalUserConsentChange[]>, GetExternalUserConsentHistoryRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/external-users/${request.userId}/consent`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.sessionId ? { sessionId: request.sessionId } : {}),
            ...(request.roomId ? { roomId: request.roomId } : {}),
          },
        }
      },
      providesTags: (_, __, request) => [{ type: 'ExternalUserConsentHistory', id: request.userId }],
    }),
    updateExternalUserConsent: builder.mutation<BaseResponse<ExternalUserConsentChange>, UpdateExternalUserConsentRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/external-users/${request.userId}/consent`,
        method: 'POST',
        body: removeEmptyItems({
          newValue: request.newValue,
          oldValue: request.oldValue,
          roomId: request.roomId,
          sessionId: request.sessionId,
          changedByUserId: request.changedByUserId,
          reason: request.reason,
          attachmentFileId: request.attachmentFileId,
          type: request.type,
        }),
      }),
      invalidatesTags: (_, __, request) => [{ type: 'ExternalUserConsentHistory', id: request.userId }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(mediaApi.util.invalidateTags(['RoomSessionById']))
      },
    }),
    searchExternalUsers: builder.query<BasePageResponse<User[]>, SearchExternalUserRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/external-users/search`,
          params: {
            term: request.term,
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.ownerId ? { ownerId: request.ownerId } : {}),
            ...(request.orderBy ? { orderBy: request.orderBy } : {}),
          },
        }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetExternalUsersQuery,
  useGetAllExternalUsersQuery,
  useLazyGetExternalUserByIdQuery,
  useLazyGetAllExternalUsersQuery,
  useGetExternalUserByIdQuery,
  useSaveExternalUserByIdMutation,
  useDeleteExternalUserByIdMutation,
  useUpdateExternalUserRolesMutation,
  useCreateExternalUserMutation,
  useGetExternalUserConsentHistoryQuery,
  useUpdateExternalUserConsentMutation,
  useSearchExternalUsersQuery,
  useLazySearchExternalUsersQuery,
} = externalUserApi
